import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import GuildEmblem from './GuildEmblem';
import Modal from '../Shared/Modal';
import styles from './GuildProfile.module.css';
import { formatDate } from '../../utils/dateFormat';

interface GuildMaster {
  id: number;
  username: string;
  displayName: string | null;
}

interface Guild {
  id: number;
  name: string;
  tag: string;
  description: string | null;
  motto: string | null;
  level: number;
  exp: number;
  totalMembers: number;
  maxMembers: number;
  leaguePoints: number;
  leagueWins: number;
  leagueLosses: number;
  guildMaster: GuildMaster;
  emblemAnimalIcon: string;
  emblemAnimalHue: number;
  emblemAnimalSaturation: number;
  emblemAnimalBrightness: number;
  emblemShieldType: string;
  createdAt: string;
}

const GuildProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [guild, setGuild] = useState<Guild | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInGuild, setUserInGuild] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [modalState, setModalState] = useState<{
    show: boolean;
    title: string;
    message: React.ReactNode;
    type: 'alert' | 'confirm';
  }>({
    show: false,
    title: '',
    message: '',
    type: 'alert'
  });

  useEffect(() => {
    fetchGuildProfile();
    if (user) {
      checkUserMembership();
    }
  }, [id, user]);

  const fetchGuildProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/guild/${id}`, { withCredentials: true });
      setGuild(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load guild profile');
    } finally {
      setLoading(false);
    }
  };

  const checkUserMembership = async () => {
    if (!user || !id) return;
    
    try {
      // Check if user is in any guild
      const myGuildResponse = await axios.get('/api/guild/my-guild', { withCredentials: true });
      if (myGuildResponse.data.guild) {
        setUserInGuild(true);
      }
      
      // Check if user has a pending application to this guild
      const applicationCheckResponse = await axios.get(`/api/guild/applications/check/${id}`, { withCredentials: true });
      setHasApplied(applicationCheckResponse.data.hasPending);
    } catch (err) {
      setUserInGuild(false);
      setHasApplied(false);
    }
  };

  const handleApplyToGuild = async () => {
    if (!guild) return;
    
    setApplying(true);
    try {
      await axios.post(`/api/guild/${guild.id}/apply`, {
        message: applicationMessage || null
      }, { withCredentials: true });
      
      setHasApplied(true);
      setShowApplicationForm(false);
      setApplicationMessage('');
      setModalState({
        show: true,
        title: 'Success',
        message: 'Application submitted successfully!',
        type: 'alert'
      });
    } catch (err: any) {
      setModalState({
        show: true,
        title: 'Error',
        message: err.response?.data?.message || 'Failed to submit application',
        type: 'alert'
      });
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <div className={styles.container}>Loading guild profile...</div>;
  }

  if (error || !guild) {
    return <div className={styles.container}>
      <div className={styles.error}>{error || 'Guild not found'}</div>
    </div>;
  }

  const winRate = guild.leagueWins + guild.leagueLosses > 0
    ? ((guild.leagueWins / (guild.leagueWins + guild.leagueLosses)) * 100).toFixed(1)
    : '0.0';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <GuildEmblem
          animalIcon={guild.emblemAnimalIcon}
          animalHue={guild.emblemAnimalHue}
          animalSaturation={guild.emblemAnimalSaturation}
          animalBrightness={guild.emblemAnimalBrightness}
          shieldType={guild.emblemShieldType}
          size="large"
          className={styles.emblem}
        />
        <div className={styles.headerInfo}>
          <h1 className={styles.guildName}>[{guild.tag}] {guild.name}</h1>
          <p className={styles.guildMaster}>
            Led by {guild.guildMaster.displayName || guild.guildMaster.username}
          </p>
          <div className={styles.level}>
            Level {guild.level} • {guild.exp} XP
          </div>
          
          {user && !userInGuild && !hasApplied && (
            <div className={styles.applySection}>
              {!showApplicationForm ? (
                <button 
                  className={styles.applyButton}
                  onClick={() => setShowApplicationForm(true)}
                >
                  Apply to Join
                </button>
              ) : (
                <div className={styles.applicationForm}>
                  <textarea
                    className={styles.messageInput}
                    placeholder="Optional message to guild leaders..."
                    value={applicationMessage}
                    onChange={(e) => setApplicationMessage(e.target.value)}
                    rows={3}
                    maxLength={500}
                  />
                  <div className={styles.formActions}>
                    <button
                      className={styles.submitButton}
                      onClick={handleApplyToGuild}
                      disabled={applying}
                    >
                      {applying ? 'Submitting...' : 'Submit Application'}
                    </button>
                    <button
                      className={styles.cancelButton}
                      onClick={() => {
                        setShowApplicationForm(false);
                        setApplicationMessage('');
                      }}
                      disabled={applying}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {user && !userInGuild && hasApplied && (
            <div className={styles.pendingApplication}>
              Application Pending
            </div>
          )}
          
          {hasApplied && (
            <div className={styles.appliedNotice}>
              ✓ Application submitted! Guild leaders will review it soon.
            </div>
          )}
        </div>
      </div>

      {guild.motto && (
        <div className={styles.motto}>
          <h3>Guild Motto</h3>
          <p>"{guild.motto}"</p>
        </div>
      )}

      {guild.description && (
        <div className={styles.description}>
          <h3>About</h3>
          <p>{guild.description}</p>
        </div>
      )}

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Members</div>
          <div className={styles.statValue}>{guild.totalMembers} / {guild.maxMembers}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>League Points</div>
          <div className={styles.statValue}>{guild.leaguePoints}</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>League Record</div>
          <div className={styles.statValue}>
            {guild.leagueWins}W - {guild.leagueLosses}L
          </div>
          <div className={styles.statSubtext}>{winRate}% win rate</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statLabel}>Established</div>
          <div className={styles.statValue}>
            {formatDate(guild.createdAt)}
          </div>
        </div>
      </div>

      <Modal
        show={modalState.show}
        title={modalState.title}
        message={modalState.message}
        onClose={() => setModalState({ ...modalState, show: false })}
      />
    </div>
  );
};

export default GuildProfile;
